import type { ApiResponse } from 'src/contracts/common';
import type { NotificationCollectionResponseDto } from 'src/contracts/notification';

import { apiClient } from 'src/lib/api-client';

// API Client for notifications
export const notificationApi = {
  /**
   * Fetch user notifications with pagination
   */
  getNotifications: async (page = 1, pageSize = 10): Promise<NotificationCollectionResponseDto> => {
    try {
      const response = await apiClient.get<NotificationCollectionResponseDto>(
        `/notifications?page=${page}&pageSize=${pageSize}`
      );
      return response;
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      return {
        isSuccess: false,
        errorCode: 'FETCH_ERROR',
        message: error.message || 'Failed to fetch notifications',
        items: [],
        totalCount: 0,
        unreadCount: 0,
        pageNumber: 1,
        pageSize: 20,
        totalPages: 0,
        hasPreviousPage: false,
        hasNextPage: false,
      };
    }
  },

  /**
   * Get the count of unread notifications
   */
  getUnreadCount: async (): Promise<ApiResponse<number>> => {
    try {
      const response = await apiClient.get<ApiResponse<number>>('/notifications/count');
      return response;
    } catch (error: any) {
      console.error('Error fetching unread notification count:', error);
      return {
        isSuccess: false,
        errorCode: 'FETCH_ERROR',
        message: error.message || 'Failed to fetch unread notification count',
        data: 0,
      };
    }
  },

  /**
   * Mark a notification as read
   */
  markAsRead: async (notificationId: string): Promise<ApiResponse<boolean>> => {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        `/notifications/${notificationId}/read`,
        {}
      );
      return response;
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      return {
        isSuccess: false,
        errorCode: 'UPDATE_ERROR',
        message: error.message || 'Failed to mark notification as read',
        data: false,
      };
    }
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<ApiResponse<boolean>> => {
    try {
      const response = await apiClient.put<ApiResponse<boolean>>(
        '/notifications/read-all',
        {}
      );
      return response;
    } catch (error: any) {
      console.error('Error marking all notifications as read:', error);
      return {
        isSuccess: false,
        errorCode: 'UPDATE_ERROR',
        message: error.message || 'Failed to mark all notifications as read',
        data: false,
      };
    }
  },

  /**
   * Delete a specific notification
   */
  deleteNotification: async (notificationId: string): Promise<ApiResponse<boolean>> => {
    try {
      const response = await apiClient.delete<ApiResponse<boolean>>(
        `/notifications/${notificationId}`
      );
      return response;
    } catch (error: any) {
      console.error('Error deleting notification:', error);
      return {
        isSuccess: false,
        errorCode: 'DELETE_ERROR',
        message: error.message || 'Failed to delete notification',
        data: false,
      };
    }
  },
};

export default notificationApi;
